import { useEffect } from "react";

/**
 * Client-side document head control for routes that are not prerendered.
 *
 * Public, indexable routes get their title, description, canonical and JSON-LD
 * baked into the served HTML by `scripts/prerender.mjs`. Do not use this hook
 * for those, because metadata that only appears after JavaScript runs is
 * invisible to a first-wave crawl.
 *
 * This hook exists for the opposite case: authenticated and error routes, where
 * the page should carry a route-specific title and, importantly, a robots
 * directive telling crawlers not to index it.
 *
 * Note the limitation. A `<meta name="robots">` tag is weaker than the
 * `X-Robots-Tag` response header the deployment should send, because a crawler
 * has to render the page to see it. The header is the real control and is
 * documented in `docs/deployment-seo-requirements.md` as outstanding hosting
 * work. This hook is a stopgap, not a substitute.
 */
export function useHeadMeta({ title, robots }) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) {
      document.title = title;
    }

    let tag = null;
    let createdTag = false;

    if (robots) {
      tag = document.querySelector('meta[name="robots"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "robots");
        document.head.appendChild(tag);
        createdTag = true;
      }
      tag.setAttribute("content", robots);
    }

    return () => {
      document.title = previousTitle;
      if (!tag) return;
      if (createdTag) {
        tag.remove();
      } else {
        tag.removeAttribute("content");
      }
    };
  }, [title, robots]);
}
